import React, { useEffect, useState } from "react";
import {
  Drawer,
  Input,
  Select,
  DatePicker,
  Button,
  message,
  Grid,
} from "antd";
import {
  TagOutlined,
  LinkOutlined,
  DollarOutlined,
  FlagOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { fetchCrmFieldsApi, fetchDealTypeApi } from "../../../../apiservice/crm";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeads } from "../../Leads/leadThunk";
import { createDeal } from "../dealThunk";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { formatDayjsToUtc } from "../../../../lib/functions";
import "../../crmSharedStyle.css";

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div className="crm-section-title">{title}</div>
    {children}
  </div>
);

const FieldLabel = ({ icon, children }) => (
  <div className="crm-field-label">
    {icon}
    {children}
  </div>
);

const CreateDealDrawer = ({ open, onClose, onSubmit, selectedLead }) => {
  const [form, setForm] = useState({});
  const [dealTypes, setDealTypes] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});

  const dispatch = useDispatch();
  const contacts = useSelector(s => s.leads.list)
  const authInfo = useSelector(selectAuthInfo);
  const screens = Grid.useBreakpoint();

  useEffect(() => {
      dispatch(fetchLeads());      
    }, []); 

  useEffect(() => {
    if (open && selectedLead) {
      setForm(prev => ({
        ...prev,
        contact_id: selectedLead.id,
      }));
    }
  }, [open, selectedLead]);  

  useEffect(() => {
    const loadFields = async () => {
      try {
        const res = await fetchCrmFieldsApi({ module: "deal" });        
         setCustomFields(res.data || []);
      } catch (err) {
        console.log("custom field fetch error", err);
      }
    };
    const loadDealType = async () => {
      try {
        const res = await fetchDealTypeApi();        
         setDealTypes(res.data || []);
      } catch (err) {
        console.log("deal type fetch error", err);
      }
    };
    loadFields();
    loadDealType();
  }, []);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleCustomChange = (fieldId, value) => {
    setCustomValues((prev) => ({
        ...prev,
        [fieldId]: value,
    }));
    };

  const handleSubmit = async () => {
    if (!form.title) return message.warning("Enter deal name");

    const payload = {
      ...form,      
      start_date: form.start_date || null,
      expected_close_date: form.expected_close_date || null,
      end_date: form.end_date || null,
      custom_fields: Object.entries(customValues).map(
        ([field_id, value]) => ({
            field_id,
            value,
        })
        ),
      currency: authInfo?.currency,
    };

    try {
        const res = await dispatch(createDeal(payload)).unwrap();

        setForm({});
        setCustomValues({});
        message.success("Deal created 🚀");
        onSubmit?.();
        onClose();
        
    } catch (error) {
      message.error("Failed ❌");  
      console.error(error);
    }
    
  };

  

  return (
    <Drawer
    open={open}
    destroyOnClose
    onClose={onClose}
    title="Create Deal"
    width={screens.md ? 480 : "100%"}
    className="crm-drawer"
    styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
  >

    {/* DEAL INFO */}
    <Section title="Deal Info">
      <FieldLabel icon={<TagOutlined />}>Deal name</FieldLabel>
      <Input
        placeholder="Deal Name"
        onChange={(e) => handleChange("title", e.target.value)}
      />

      <div style={{ marginTop: 12 }}>
        <FieldLabel icon={<LinkOutlined />}>Lead</FieldLabel>
        <Select
          value={form.contact_id}
          placeholder="Select Lead"
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="label"
          onChange={(v) => handleChange("contact_id", v)}
          options={contacts.map(c => ({
            label: `${c.name} (${c.phone})`,
            value: c.id
          }))}
          disabled={!!selectedLead}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel icon={<DollarOutlined />}>Amount</FieldLabel>
        <Input
          placeholder="Amount"
          type="number"
          onChange={(e) => handleChange("value", e.target.value)}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel icon={<FlagOutlined />}>Stage</FieldLabel>
        <Select
          placeholder="Stage"
          style={{ width: "100%" }}
          onChange={(v) => handleChange("stage", v)}
          options={[
            { label: "Prospecting", value: "prospecting" },
            { label: "Negotiation", value: "negotiation" },
            { label: "Won", value: "won" },
            { label: "Lost", value: "lost" },
          ]}
        />
      </div>
    </Section>

    {/* TIMELINE */}
    <Section title="Timeline">
      <div className="crm-form-row">
        <div>
          <FieldLabel icon={<CalendarOutlined />}>Start date</FieldLabel>
          <DatePicker
            placeholder="Start Date"
            style={{ width: "100%" }}
            onChange={(d) => handleChange("start_date", d ? d.format("YYYY-MM-DD") : null)}
          />
        </div>
        <div>
          <FieldLabel icon={<CalendarOutlined />}>Expected close</FieldLabel>
          <DatePicker
            placeholder="Expected Close"
            style={{ width: "100%" }}
            onChange={(d) => handleChange("expected_close_date", d ? d.format("YYYY-MM-DD") : null)}
          />
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel icon={<CalendarOutlined />}>End date</FieldLabel>
        <DatePicker
          placeholder="End Date"
          style={{ width: "100%" }}
          onChange={(d) => handleChange("end_date", d ? d.format("YYYY-MM-DD") : null)}
        />
      </div>
    </Section>

    {/* MORE INFO */}
    <Section title="More Info">
      <FieldLabel icon={<AppstoreOutlined />}>Deal type</FieldLabel>
      <Select
        placeholder="Deal Type"
        style={{ width: "100%" }}
        onChange={(v) => handleChange("deal_type_id", v)}
        options={dealTypes.map(d => ({
          label: d.name,
          value: d.id
        }))}
      />

      <div style={{ marginTop: 12 }}>
        <FieldLabel icon={<FileTextOutlined />}>Notes</FieldLabel>
        <Input.TextArea
          placeholder="Notes"
          autoSize={{
            minRows: 3,
            maxRows: 10,
          }}
          onChange={(e) => handleChange("notes", e.target.value)}
        />
      </div>
    </Section>

    {/* CUSTOM FIELDS */}
    <Section title="Additional Fields">
  {customFields
    .filter((f) => f.show_in_form)
    .map((f) => (
      <div key={f.id} style={{ marginTop: 12 }}>

        {/* LABEL */}
        <div className="crm-field-label" style={{ textTransform: "none" }}>
          {f.field_name} {f.is_required && "*"}
        </div>

        {/* TEXT */}
        {f.type === "text" && (
          <Input
            placeholder={f.field_name}
            onChange={(e) =>
              handleCustomChange(f.id, e.target.value)
            }
          />
        )}

        {/* NUMBER */}
        {f.type === "number" && (
          <Input
            type="number"
            placeholder={f.field_name}
            onChange={(e) =>
              handleCustomChange(f.id, e.target.value)
            }
          />
        )}

        {/* DROPDOWN */}
        {f.type === "dropdown" && (
          <Select
            placeholder={f.field_name}
            options={(f.options || []).map((o) => ({
              label: o,
              value: o,
            }))}
            onChange={(v) => handleCustomChange(f.id, v)}
          />
        )}

        {/* MULTI SELECT */}
        {f.type === "multi_select" && (
          <Select
            mode="multiple"
            placeholder={f.field_name}
            options={(f.options || []).map((o) => ({
              label: o,
              value: o,
            }))}
            onChange={(v) =>
              handleCustomChange(f.id, JSON.stringify(v))
            }
          />
        )}

        {/* DATE */}
        {f.type === "date" && (
          <DatePicker
            style={{ width: "100%" }}
            onChange={(d) =>
              handleCustomChange(
                f.id,
                d ? d.format("YYYY-MM-DD") : null
              )
            }
          />
        )}

        {/* DATETIME */}
        {f.type === "datetime" && (
          <DatePicker
            showTime
            style={{ width: "100%" }}
            onChange={(d) =>
              handleCustomChange(
                f.id,
                // d ? d.format("YYYY-MM-DD HH:mm:ss") : null
                d ? formatDayjsToUtc(d) : null
              )
            }
          />
        )}
      </div>
    ))}
</Section>

    <div className="crm-drawer-footer">
      <Button type="primary" size="large" block onClick={handleSubmit}>
        Create Deal
      </Button>
    </div>

  </Drawer>
  );
};

export default CreateDealDrawer;